import { notify } from "@general-backoffice/core";
import { FormInput } from "@general-backoffice/core/index";
// import { Button } from "reactstrap";
import type { ReferrerType } from "@investhinks/api-client";
import React, { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Alert } from "reactstrap";

import { signUp } from "../../../api";
import { useFormHandlers } from "../../../hooks/strapi";
import useCookie from "../../../hooks/useCookie";
import useCountryRegionFormOptions from "../../../hooks/useCountryRegionFormOptions";
// import SignUpDisclaimer from "../../../components/legal/SignUpDisclaimer";
import logo from "../../../media/branding/logoLight.png";
import { login } from "../../../redux/reducers/session/actions";
import getErrorMessage from "../../../utils/getErrorMessage";
import { TypedAuthFormPage } from "../../../utils/typedLib";

// Local type definitions
interface SignUpData {
  name?: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
  referralCode?: string | null;
  referrerType?: ReferrerType;
  country?: string | null;
}

interface SignUpErrors {
  name?: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
  [key: string]: string | undefined;
}

interface SignUpProps {
  asDistributor?: boolean;
}

interface SelectOption {
  label?: string;
  value?: string | null;
}

const passwordPattern = "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-_]).{8,}$";
const passwordRegex = new RegExp(passwordPattern);

const DISTRIBUTOR_SIGNUP_REFERRER_TYPE = "DISTRIBUTOR"; // Will be "REQUEST_DISTRIBUTOR"

const SignUp: React.FC<SignUpProps> = ({ asDistributor = false }) => {
  const { t, i18n } = useTranslation("auth", { keyPrefix: "signup" });

  //       Referral stuff
  const [searchParams] = useSearchParams();

  // refCode
  const refCode = searchParams.get("refCode");
  const [refCodeCookie, updateRefCodeCookie] = useCookie("investhinks_referralCode", "");
  useEffect(() => {
    if (refCode) updateRefCodeCookie(refCode, 30);
  }, [refCode, updateRefCodeCookie]);

  // ref
  const ref = searchParams.get("ref");
  const [refCookie, updateRefCookie] = useCookie("investhinks_referral", "");
  useEffect(() => {
    if (ref) updateRefCookie(ref, 30);
  }, [ref, updateRefCookie]);

  // const { t } = useTranslation()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [billingData, setBillingData] = useState(null)
  const [data, setData] = useState<SignUpData | null>({
    referralCode: refCode,
    ...(asDistributor && { referrerType: DISTRIBUTOR_SIGNUP_REFERRER_TYPE }), // Will be "REQUEST_DISTRIBUTOR"
  });
  const [errors, setErrors] = useState<SignUpErrors>({});
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [isLoading, setLoading] = useState(false);
  const {
    name,
    email,
    password,
    passwordConfirm,
    referralCode,
    referrerType,
    country,
    // accountType
  } = data ?? {};
  // TODO: Fix type assertion - useCountryRegionFormOptions expects region but we only use country
  const { country: currCountry, countryOptions } = useCountryRegionFormOptions({
    country: country ?? undefined,
    region: undefined,
  });

  const countryFilterOption = useMemo(() => {
    const labels = countryOptions.map((o) => o.label.toLowerCase());
    return (option: { label: string }, inputValue: string) => {
      if (!inputValue) return true;
      const input = inputValue.toLowerCase();
      const label = option.label.toLowerCase();
      const hasStartsWithMatch = labels.some((l) => l.startsWith(input));
      return hasStartsWithMatch ? label.startsWith(input) : label.includes(input);
    };
  }, [countryOptions]);

  useEffect(() => {
    const baseErrors: SignUpErrors = {};
    if (password && passwordConfirm && password !== passwordConfirm)
      baseErrors.passwordConfirm = t("passwordMatchError");
    if (password && !passwordRegex.test(password)) baseErrors.password = t("passwordPatternError");
    setErrors(baseErrors);
    // Clear the banner once the field that caused it is valid again.
    if (!Object.values(baseErrors).filter(Boolean).length) setSubmitError(null);
  }, [password, passwordConfirm, t]);

  const { handleInputEvent, handleInputChange } = useFormHandlers(setData);

  // Sync referralCode from cookie (only when cookie has a value)
  useEffect(() => {
    if (refCodeCookie && referralCode !== refCodeCookie) handleInputChange("referralCode", refCodeCookie);
  }, [refCodeCookie, referralCode, handleInputChange]);

  const onSubmit = () => {
    // The guard has to run before the loading flag: returning early after
    // setting it skipped the `.finally`, leaving every field permanently
    // disabled with no explanation until the page was reloaded.
    const validationErrors = Object.values(errors).filter(Boolean);
    if (validationErrors.length) {
      setSubmitError(validationErrors[0] as string);
      return;
    }
    if (!termsAccepted) {
      setSubmitError(t("form.termsRequired"));
      return;
    }
    setSubmitError(null);
    setLoading(true);
    const detectedLang = i18n.language.split("-")[0].toLowerCase();
    const lang = detectedLang === "en" || detectedLang === "es" ? detectedLang : "en";
    signUp({
      email: email || "",
      password: password || "",
      referralCode: referralCode || undefined,
      referrerCode: refCookie || undefined,
      name: name || "",
      country: country || undefined,
      referrerType,
      language: lang,
    })
      .then(({ data }) => {
        const { jwt, user } = data;
        dispatch(login(jwt, { ...user }));
        notify.success(t("successMessage"), { icon: <i className="fas fa-handshake ni-lg" /> });
      })
      .catch((err: unknown) => {
        const message = getErrorMessage(err, t("form.genericError"));
        setSubmitError(message);
        notify.error(message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <TypedAuthFormPage
      title={
        <>
          <span>{t("title")}</span>
          <img src={logo} alt="" className="mt-1 mb-3" style={{ width: 600, maxWidth: "100%" }} />
        </>
      }
      lead={<div className="mb--5">{t("lead")}</div>}
      submitText={t("submitText")}
      onSubmit={async () => {
        onSubmit();
      }}
      rightLink={{
        children: t("rightLink"),
        onClick: () => navigate("/auth/login"),
      }}
      // disabled={isLoading || }
    >
      <div id="test" />

      <FormInput.Input
        onChange={handleInputEvent}
        value={name || ""}
        name="name"
        // label={t("Name")}
        label={t("form.name")}
        error={errors["name"]}
        disabled={isLoading}
        required
      />

      <FormInput.Email
        onChange={handleInputEvent}
        value={email || ""}
        name="email"
        label={t("form.email")}
        error={errors["email"]}
        disabled={isLoading}
        required
      />

      <FormInput.Select
        label={t("form.country")}
        name="country"
        value={currCountry}
        options={countryOptions}
        filterOption={countryFilterOption}
        onChange={(o: SelectOption | null) => handleInputChange("country", o?.value || null)}
        isDisabled={isLoading}
        isClearable
        required
      />

      <FormInput.Password
        pattern={passwordPattern}
        onChange={handleInputEvent}
        value={password || ""}
        name="password"
        label={t("form.password")}
        error={errors["password"]}
        disabled={isLoading}
        showStrengthBar
        required
      />

      <FormInput.Password
        pattern={passwordPattern}
        onChange={handleInputEvent}
        value={passwordConfirm || ""}
        name="passwordConfirm"
        label={t("form.passwordConfirm")}
        error={errors["passwordConfirm"]}
        disabled={isLoading}
        required
      />

      {!(refCookie && !referralCode) && (
        <div style={{ maxWidth: "90%", width: 200 }} className="mt-2 ml-auto">
          <FormInput.Input
            type="text"
            onChange={handleInputEvent}
            value={referralCode || ""}
            name="referralCode"
            label={t("form.referralCode")}
            disabled={isLoading || !!refCode || !!refCodeCookie}
            // required
          />
        </div>
      )}

      {submitError && (
        <div className="px-4" role="alert">
          <Alert color="danger" className="py-2 mb-2" style={{ fontSize: ".8125rem" }}>
            {submitError}
          </Alert>
        </div>
      )}

      <div className="px-4 pb-4">
        <FormInput.Input
          type="checkbox"
          checked={termsAccepted}
          // onChange={e => e.target.checked ? setTCModal(true) : setTermsAccepted(false)}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) setSubmitError(null);
          }}
          label={
            <span>
              <Trans
                t={t}
                i18nKey="form.t&c"
                components={{
                  termsLink: <a href="https://www.investhinks.com/legal/terms" rel="noreferrer" target="_blank" />,
                  disclaimerLink: (
                    <a href="https://www.investhinks.com/legal/disclaimer" rel="noreferrer" target="_blank" />
                  ),
                }}
              />
            </span>
          }
          disabled={isLoading}
          required
        />
      </div>
    </TypedAuthFormPage>
  );
};

export default SignUp;
